/**
 * Take-profit / stop-loss dragged off the chart's position line (SPEC §9.3; owner 2026-10-06 "I
 * want the ability to drag take profit and stop loss on every chart", Tradesea behaviour): press
 * the position's tag and drag — into the profit direction (above the average for a long, below
 * it for a short) a take-profit (limit), into the loss direction a stop-loss (stop market), for
 * the position's whole size. While dragging, a preview line shows the price, the distance from
 * the average and the P&L at that price for the whole position; the release places it (one
 * PlaceOrder, confirmed first unless one-click). Pure: the chart engine draws, the host sends.
 *
 * A position may have one TP and one SL (the gateway's position bracket, one OCO pair): a drag
 * creates the missing one; with both set the tag does not drag (their own lines move them). A
 * position protected by its entry's bracket, or with other working orders that close it, keeps
 * those (drag their lines) — the gateway refuses the same.
 */
import { openPnlCents, openTickContracts, roundHalfAway, type ExactPosition } from "@helm/core/trading";
import { MINUS, PRIVACY_MASK, formatCents } from "./format.ts";
import type { LinePnlBasis } from "./chartLines.ts";

/** Which protective orders a drag off a position line may still create. */
export type ProtectSet = "tp" | "sl" | "both";
export type ProtectKind = "tp" | "sl";

/** The TP / SL wording users see. */
export const PROTECT_NAME: Readonly<Record<ProtectKind, string>> = { tp: "TP", sl: "SL" };

/**
 * Which order a drag off a position (`side` "buy" = long) from its average `fromTicks` to
 * `toTicks` creates: the profit direction a take-profit, the loss direction a stop-loss; null at
 * the average itself. The chart engine applies the same rule (`protectKindAt`) to colour the
 * preview.
 */
export function protectKindOf(side: "buy" | "sell", fromTicks: number, toTicks: number): ProtectKind | null {
  if (!Number.isFinite(toTicks) || !Number.isFinite(fromTicks) || toTicks === fromTicks) return null;
  return toTicks > fromTicks === (side === "buy") ? "tp" : "sl";
}

/**
 * What a drag off a position line may create, from what protects it now: null when nothing (a
 * TP and an SL are both set — drag those lines). `entryBracket`: its TP / SL come from an entry's
 * bracket; `closing`: other working orders already close part of it — both are kept as they are.
 */
export function protectSetOf(p: {
  hasTp: boolean;
  hasSl: boolean;
  entryBracket: boolean;
  closing: boolean;
}): {
  set: ProtectSet | null;
  why: string;
} {
  if (p.entryBracket)
    return { set: null, why: "Protected by its entry's bracket: drag its TP / SL lines to move them" };
  if (p.closing)
    return { set: null, why: "Working orders already close part of this position: drag or cancel them" };
  if (p.hasTp && p.hasSl) return { set: null, why: "TP and SL are set: drag their lines to move them" };
  if (p.hasTp)
    return { set: "sl", why: "Drag away from the price into the loss for a stop-loss (the TP is set)" };
  if (p.hasSl) return { set: "tp", why: "Drag into the profit for a take-profit (the SL is set)" };
  return { set: "both", why: "Drag up / down from here for a take-profit or a stop-loss" };
}

/** The order a released drag places: the side that closes the position, its whole size. */
export type ProtectOrder = {
  kind: ProtectKind;
  side: "buy" | "sell";
  type: "limit" | "stop";
  qty: number;
  priceTicks: number;
};

/**
 * The TP / SL for a position of `netQty` (signed) dropped at `priceTicks`, checked against the
 * live best bid / ask: refused when it would execute at once — a TP at or through the bid (long:
 * a sell limit must rest above the best bid; short: a buy limit below the best ask), an SL at or
 * through it (long: a sell stop below the best bid; short: a buy stop above the best ask) — or
 * when it is not the kind the drag direction makes.
 */
export function protectOrder(o: {
  kind: ProtectKind;
  netQty: number;
  priceTicks: number;
  bid: number | null;
  ask: number | null;
}): { ok: true; order: ProtectOrder } | { ok: false; reason: string } {
  if (!Number.isSafeInteger(o.netQty) || o.netQty === 0) return { ok: false, reason: "No open position" };
  if (!Number.isSafeInteger(o.priceTicks) || o.priceTicks <= 0)
    return { ok: false, reason: "Drop it on a positive price" };
  if (o.bid === null || o.ask === null) return { ok: false, reason: "No best bid/ask yet" };
  const long = o.netQty > 0;
  const side = long ? "sell" : "buy";
  const p = o.priceTicks;
  if (o.kind === "tp") {
    if (long ? p <= o.bid : p >= o.ask)
      return {
        ok: false,
        reason: `A take-profit ${long ? "at or below the best bid" : "at or above the best ask"} would fill at once`,
      };
  } else if (long ? p >= o.bid : p <= o.ask) {
    return {
      ok: false,
      reason: `A stop-loss ${long ? "at or above the best bid" : "at or below the best ask"} would trigger at once`,
    };
  }
  return {
    ok: true,
    order: {
      kind: o.kind,
      side,
      type: o.kind === "tp" ? "limit" : "stop",
      qty: Math.abs(o.netQty),
      priceTicks: p,
    },
  };
}

/**
 * Where a TP / SL goes for a position (`side` "buy" = long): "a TP goes above the average".
 * The wording of a drag off the TP / SL chip into the other side (refused).
 */
export function protectSideRule(side: "buy" | "sell", kind: ProtectKind): string {
  const above = (side === "buy") === (kind === "tp");
  return `a${kind === "sl" ? "n" : ""} ${PROTECT_NAME[kind]} goes ${above ? "above" : "below"} the average`;
}

/** `num / den` with `dec` places, halves away from zero, signed ("+3.25", "−0.50", "0.00"). */
function signedRatio(num: bigint, den: bigint, dec: number): string {
  const scale = 10n ** BigInt(dec);
  const q = roundHalfAway(num * scale, den);
  const neg = q < 0n;
  const m = neg ? -q : q;
  const int = m / scale;
  const frac = m % scale;
  const body = dec > 0 ? `${int.toString()}.${frac.toString().padStart(dec, "0")}` : int.toString();
  return q === 0n ? body : `${neg ? MINUS : "+"}${body}`;
}

/**
 * The preview line's label while a TP / SL is dragged off a position line:
 * "TP 21,460.00 · +40t · +10.00 pts · +$600.00" — the dropped price, its distance from the
 * average entry in the trade's favour (ticks, points) and the P&L at that price for the whole
 * position (cash, rounded half away from zero to the cent; masked in privacy mode). A direction
 * the line cannot create says so: "TP set · drag the TP line".
 *
 * `chip`: dragged off the position tag's TP or SL chip (Tradesea) — that order whichever way it
 * goes; on the wrong side of the average the label says it is refused ("TP refused · a TP goes
 * above the average").
 */
export function protectGhostLabel(
  line: {
    side: "buy" | "sell";
    priceTicks: number;
    protect: ProtectSet | null;
    pnlBasis: LinePnlBasis | null;
  },
  toTicks: number,
  priceText: string,
  chip?: ProtectKind,
): string {
  const dir = protectKindOf(line.side, line.priceTicks, toTicks);
  if (chip !== undefined && dir !== null && dir !== chip)
    return `${PROTECT_NAME[chip]} refused · ${protectSideRule(line.side, chip)}`;
  const kind = chip ?? dir;
  if (kind === null || dir === null)
    return chip === undefined ? `${priceText} · drag up or down` : `${PROTECT_NAME[chip]} ${priceText}`;
  const name = PROTECT_NAME[kind];
  if (!(line.protect === "both" || line.protect === kind)) return `${name} set · drag the ${name} line`;
  const parts = [`${name} ${priceText}`];
  const b = line.pnlBasis;
  if (b && b.netQty !== 0) {
    const pos: ExactPosition = { netQty: b.netQty, openCostTicks: BigInt(b.openCostTicks) };
    const q = BigInt(Math.abs(b.netQty));
    const tc = openTickContracts(pos, toTicks); // Σ ticks over the |q| contracts
    parts.push(`${signedRatio(tc, q, tc % q === 0n ? 0 : 2)}t`);
    if (b.tick) {
      const dec = Math.max(0, Math.min(8, b.tick.decimals));
      parts.push(`${signedRatio(tc * BigInt(b.tick.tickNum), q * BigInt(b.tick.tickDen), dec)} pts`);
    }
    if (b.tickValueCents > 0) {
      const cents = roundHalfAway(openPnlCents(pos, toTicks, b.tickValueCents) * BigInt(b.closeQty), q);
      parts.push(b.privacy ? PRIVACY_MASK : formatCents(cents, { signed: true }));
    }
  }
  return parts.join(" · ");
}
